import { useMemo } from "react"

import { containsOrEquals } from "shared/lib/arrayUtils"
import { useAuth } from "shared/lib/useAuth"
import { Project } from "shared/types/Project"
import { SelectOption } from "shared/types/Select"

import { FAVORITES_PROJECT_ID } from "entities/favorites/api/useFavoriteMutations"
import { useProjects } from "entities/projects/api/useProjects"
import { useAllProjectPermissionsQuery } from "entities/projects/graphql/queries.generated"

interface UseProjectOptionsArgs {
  excludedProjectIDs?: string | string[]
}

export function useProjectOptions(args?: UseProjectOptionsArgs) {
  const { isAuthenticated } = useAuth()
  const { projects, loading: isLoadingProjects } = useProjects()

  const { data: permissionsData, loading: isLoadingPermissions } =
    useAllProjectPermissionsQuery({ skip: !isAuthenticated })

  const editableProjectIDs = useMemo(() => {
    const ids = new Set<string>([FAVORITES_PROJECT_ID])
    permissionsData?.allProjectPermissions?.forEach(p => {
      if (p.permissions?.edit_project) ids.add(p.resourceId)
    })
    return ids
  }, [permissionsData])

  const options: SelectOption<string, Project>[] = useMemo(
    () =>
      projects?.reduce(
        (acc, project) =>
          containsOrEquals(args?.excludedProjectIDs, project.id) ||
          !editableProjectIDs.has(project.id)
            ? acc
            : [
                ...acc,
                {
                  value: project.id,
                  label: project.title,
                  data: project
                }
              ],
        [] as SelectOption<string, Project>[]
      ) || [],
    [projects, editableProjectIDs]
  )

  return {
    projects,
    options,
    isLoading: isLoadingProjects || isLoadingPermissions
  }
}
